import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, Bot, User, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatModal = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const modalRef = useRef(null);
  const { theme } = useTheme();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `Welcome to Murtaza AI!\n\nI'm here to help with Data & AI questions, especially around Databricks. Ask me about:\n\n• Murtaza's skills, experience, or projects\n• Databricks architecture & best practices\n• Data platforms, Delta Lake, MLflow\n• Cloud solutions (Azure, AWS, GCP)\n\nHow can I assist you today?`,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSend = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: inputMessage,
        session_id: sessionId
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date(),
        is_static: response.data.is_static
      };

      setMessages(prev => [...prev, assistantMessage]);
      setSessionId(response.data.session_id);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatMessage = (text) => {
    const formatted = text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br/>');
    return DOMPurify.sanitize(formatted);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        ref={modalRef}
        className={`relative w-full max-w-4xl h-[90vh] sm:h-[80vh] border rounded-lg shadow-2xl flex flex-col ${
          theme === 'dark' 
            ? 'bg-[#1B3139] border-white/10' 
            : 'bg-white border-gray-200'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b flex-shrink-0 ${
          theme === 'dark' 
            ? 'border-white/10 bg-white/5' 
            : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-center space-x-3">
            <div className="p-1.5 sm:p-2 bg-[#FF3621]/10 border border-[#FF3621]/30 rounded-lg">
              <Bot className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF3621]" />
            </div>
            <div>
              <h2 
                className={`text-lg sm:text-xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Ask Murtaza!
              </h2>
              <p 
                className={`text-xs sm:text-sm hidden sm:block ${
                  theme === 'dark' ? 'text-white/60' : 'text-gray-500'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Data & AI Expert • Powered by Claude
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-gray-200'
            }`}
            aria-label="Close chat"
          >
            <X className={`h-5 w-5 ${
              theme === 'dark' ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'
            }`} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-3 sm:py-4 space-y-3 sm:space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex items-start space-x-2 sm:space-x-3 ${
                message.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              {message.role === 'assistant' && (
                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-[#FF3621]/10 border border-[#FF3621]/30 rounded-lg flex items-center justify-center">
                  <Bot className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#FF3621]" />
                </div>
              )}
              
              <div
                className={`max-w-[85%] sm:max-w-[75%] rounded-lg p-3 sm:p-4 ${
                  message.role === 'user'
                    ? 'bg-[#FF3621] text-white'
                    : theme === 'dark'
                      ? 'bg-black/30 border border-white/10 text-white/90'
                      : 'bg-gray-100 border border-gray-200 text-gray-800'
                }`}
              >
                <div 
                  className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                  dangerouslySetInnerHTML={{
                    __html: formatMessage(message.content)
                  }}
                />
                {message.is_static && (
                  <div className={`mt-2 pt-2 border-t ${
                    theme === 'dark' ? 'border-white/10' : 'border-gray-200'
                  }`}>
                    <span 
                      className={`text-xs font-mono ${
                        theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                      }`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      ⚡ Quick Response
                    </span>
                  </div>
                )}
              </div>

              {message.role === 'user' && (
                <div className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 border rounded-lg flex items-center justify-center ${
                  theme === 'dark' 
                    ? 'bg-white/10 border-white/20' 
                    : 'bg-gray-200 border-gray-300'
                }`}>
                  <User className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-700'
                  }`} />
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex items-start space-x-2 sm:space-x-3">
              <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-[#FF3621]/10 border border-[#FF3621]/30 rounded-lg flex items-center justify-center">
                <Bot className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#FF3621]" />
              </div>
              <div className={`rounded-lg p-3 sm:p-4 ${
                theme === 'dark' 
                  ? 'bg-black/30 border border-white/10' 
                  : 'bg-gray-100 border border-gray-200'
              }`}>
                <div className="flex items-center space-x-2">
                  <Loader2 className="h-4 w-4 text-[#FF3621] animate-spin" />
                  <span 
                    className={`text-xs sm:text-sm ${
                      theme === 'dark' ? 'text-white/60' : 'text-gray-500'
                    }`}
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                  >
                    Thinking...
                  </span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className={`px-3 sm:px-6 py-3 sm:py-4 border-t flex-shrink-0 ${
          theme === 'dark' 
            ? 'border-white/10 bg-white/5' 
            : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-end space-x-2 sm:space-x-3">
            <div className="flex-1">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about Databricks, Data & AI..."
                rows={2}
                disabled={isLoading}
                className={`w-full border rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:border-[#FF3621] focus:ring-1 focus:ring-[#FF3621] resize-none disabled:opacity-50 ${
                  theme === 'dark' 
                    ? 'bg-black/30 border-white/20 text-white placeholder-white/40' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              />
              <p 
                className={`hidden sm:block text-xs mt-2 font-mono ${
                  theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                }`}
                style={{ fontFamily: 'DM Mono, monospace' }}
              >
                Press Enter to send, Shift+Enter for new line
              </p>
            </div>
            <Button
              onClick={handleSend}
              disabled={!inputMessage.trim() || isLoading}
              className="bg-[#FF3621] text-white hover:bg-[#FF3621]/90 px-4 sm:px-6 py-4 sm:py-6 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className={`px-3 sm:px-6 py-2 sm:py-3 border-t flex-shrink-0 ${
          theme === 'dark' 
            ? 'border-white/10 bg-black/20' 
            : 'border-gray-200 bg-gray-100'
        }`}>
          <div className={`flex items-center justify-between text-xs ${
            theme === 'dark' ? 'text-white/40' : 'text-gray-500'
          }`}
               style={{ fontFamily: 'DM Mono, monospace' }}>
            <span>Powered by Claude Sonnet 4.6</span>
            <span className="hidden sm:inline">Session: {sessionId ? sessionId.substring(0, 8) : 'New'}...</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at body level, avoiding stacking context issues
  return createPortal(modalContent, document.body);
};

export default ChatModal;
